import {
  CustomerClassificationQueryParams,
  useCustomerClassificationQuery,
} from '@/src/api/use-customer-classification-query';
import { useSupplyPointsQuery } from '@/src/api/kapi/use-supply-points-query';
import { useAccountContext } from '@ovotech/ui-tools';
import { useProfileQueryKapi } from '@/src/api/kapi/use-profile-query';
import { mapOhsClassifySearchAddress } from '@/src/utils/map-ohs-classify-search-address';

/**
 * This hook queries the customer classification service endpoint which checks if a customer is active, inactive, or recent.
 *
 * @returns string classification - The classification of the customer, which can be 'active', 'inactive', or 'recent'.
 * @returns boolean customerActiveOrRecent - A boolean indicating if the customer is either active or recent.
 */
export const useCustomerClassification = () => {
  const { accountId } = useAccountContext();
  const profile = useProfileQueryKapi();
  const supplyPoints = useSupplyPointsQuery();
  const address = mapOhsClassifySearchAddress([
    ...(supplyPoints.data?.account?.accountSupplyPoints ?? []),
  ]);

  const profileReady = !!profile?.data?.customer_nextV1;
  const supplyPointsReady = !!supplyPoints.data?.account?.accountSupplyPoints;

  const enabled = !!accountId && profileReady && supplyPointsReady;

  const params: CustomerClassificationQueryParams = {
    ovoAccountId: accountId!,
    firstname: profile?.data?.customer_nextV1?.givenName,
    lastname: profile?.data?.customer_nextV1?.familyName,
    email: profile?.data?.customer_nextV1?.contactEmail,
    phoneNumber: profile?.data?.customer_nextV1?.contactPhoneNumbers[0],
    addressline1: address?.addressLine1,
    postcode: address?.postCode,
  };

  const { data } = useCustomerClassificationQuery(params, { enabled });

  const customerActiveOrRecent =
    data?.classification === 'active' || data?.classification === 'recent';

  return {
    classification: data?.classification || 'inactive',
    customerActiveOrRecent,
  };
};
